import type { CustomSEO, SEOPageType } from '@iheartjane/data-access';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  Accordion,
  AlertIcon,
  Card,
  Flex,
  Hide,
  Link,
  Tabs,
  Typography,
} from '@iheartjane/reefer';
import { titleCase } from '@iheartjane/utils';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { CATEGORIES_ORDER } from '../utils/categories';
import { NAV_LINKS, SEO_URLS } from './defaultSeoConstants';
import { SeoForm } from './SeoForm';

import styles from './PageSeo.module.css';

interface PageSeoProps {
  defaults?: CustomSEO[SEOPageType];
  faviconUrl?: string | null;
  menuUrl: string;
  pageDescription: string;
  pageType: SEOPageType;
  seoVariables: Record<string, string | undefined>;
}
type ActiveTab = 'seo-metadata' | 'on-page-seo';

const ON_PAGE_SEO_TYPES = ['category'];
const METADATA_TITLE_HINT =
  'We recommend 50–60 characters; include a primary keyword and your store name. Example: Buy Flower in Santa Cruz | Field Trip';
const METADATA_DESCRIPTION_HINT =
  "We recommend 150–160 characters; lead with a keyword, describe what's on the page, and include a soft call to action";
const ON_PAGE_TITLE_HINT =
  'We recommend 40–70 characters; clear, descriptive, non-generic. Example: Organic Pre-Rolls in Chicago';
const ON_PAGE_DESCRIPTION_HINT =
  'We recommend 250–500 characters; include your primary keyword within the first 100 characters and add informational value';

const LabelWithError = ({
  error,
  label,
}: {
  error: boolean;
  label: string;
}) => {
  return (
    <Flex alignItems="center">
      {error && <AlertIcon mr={8} size="sm" color="error" />}
      <Typography variant="body-semibold">{label}</Typography>
    </Flex>
  );
};

export const PageSeo = ({
  defaults,
  faviconUrl,
  menuUrl,
  pageDescription,
  pageType,
  seoVariables,
}: PageSeoProps) => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('seo-metadata');
  const {
    setValue,
    formState: { errors },
  } = useFormContext<CustomSEO>();
  const pageLabel = NAV_LINKS.find((link) => link.value === pageType)?.label;
  const onPageSeoEnabled = useFlag(FLAGS.c2CategoryOnPageSeo);
  const hasOnPageSeo = onPageSeoEnabled && ON_PAGE_SEO_TYPES.includes(pageType);

  const restoreDefault = () => {
    if (activeTab === 'on-page-seo') {
      for (const kind of CATEGORIES_ORDER) {
        setValue(`onPage.${kind}.title`, '', {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue(`onPage.${kind}.description`, '', {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    } else {
      setValue(`${pageType}.title`, defaults?.title ?? '', {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue(`${pageType}.description`, defaults?.description ?? '', {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  return (
    <Card id={pageType}>
      <Card.Content p={40}>
        <Flex flexDirection="column" gap={24}>
          <Flex justifyContent="space-between" alignItems="center">
            <Typography variant="h3-semibold">{pageLabel}</Typography>
            <Link color="grays-mid" onClick={restoreDefault}>
              Restore default
            </Link>
          </Flex>

          {hasOnPageSeo && (
            <Tabs<ActiveTab>
              className={styles['bottom-border']}
              onChange={setActiveTab}
              value={activeTab}
            >
              <Tabs.Tab
                label={
                  <LabelWithError
                    error={!!errors[pageType]}
                    label="SEO Metadata"
                  />
                }
                value="seo-metadata"
              />
              <Tabs.Tab
                label={
                  <LabelWithError
                    error={!!errors['onPage']}
                    label="On-page SEO"
                  />
                }
                value="on-page-seo"
              />
            </Tabs>
          )}

          <Flex flexDirection="column" gap={24}>
            <Hide
              data-testid="seo-metadata"
              isHidden={activeTab !== 'seo-metadata'}
            >
              <Typography as="div" mb={24}>
                {hasOnPageSeo && (
                  <Typography variant="body-semibold">SEO Metadata</Typography>
                )}
                Creating descriptive titles and descriptions for{' '}
                {pageDescription} (
                <span className={styles['code-block']}>
                  {SEO_URLS[pageType]}
                </span>
                ) is crucial for search engines. Use the listed variables to
                create a template for pages. Some variables will use dummy
                sample data and the default shows what Jane populates currently.
                Jane&apos;s default page titles and descriptions are optimized
                to maximize store traffic from Google. Partners customizing
                their page details may experience decreased search traffic.
              </Typography>

              <SeoForm
                descriptionHint={METADATA_DESCRIPTION_HINT}
                faviconUrl={faviconUrl}
                menuUrl={menuUrl}
                pageType={pageType}
                titleHint={METADATA_TITLE_HINT}
                variables={seoVariables}
              />
            </Hide>

            {hasOnPageSeo && (
              <Hide
                data-testid="on-page-seo"
                isHidden={activeTab !== 'on-page-seo'}
              >
                <Typography as="div" mb={24}>
                  <Typography variant="body-semibold">
                    Per category — customize each page individually
                  </Typography>
                  Adds visible content directly on each category page to help
                  search engines understand the page and give shoppers more
                  context about what they&apos;ll find. Content only displays on
                  categories that are active on your menu. Leave a category
                  empty to skip it.
                </Typography>

                <Accordion>
                  {CATEGORIES_ORDER.map((kind) => (
                    <Accordion.Item key={kind} id={kind}>
                      <Accordion.Heading>
                        <Flex
                          className={styles['bottom-border']}
                          justifyContent="space-between"
                          py={24}
                        >
                          <LabelWithError
                            error={
                              !!errors['onPage'] && !!errors['onPage'][kind]
                            }
                            label={titleCase(kind)}
                          />

                          <Accordion.Indicator />
                        </Flex>
                      </Accordion.Heading>

                      <Accordion.Content py={24}>
                        <SeoForm
                          descriptionHint={ON_PAGE_DESCRIPTION_HINT}
                          pageType={`onPage.${kind}`}
                          titleHint={ON_PAGE_TITLE_HINT}
                          variables={{ ...seoVariables, category: kind }}
                        />
                      </Accordion.Content>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </Hide>
            )}
          </Flex>
        </Flex>
      </Card.Content>
    </Card>
  );
};
